import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconAlertSquareRounded, IconChevronLeft } from '@tabler/icons-react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  confirmSignUp,
  resendSignUpCode,
  signIn,
} from 'aws-amplify/auth';
import { login } from '../../../reducers/authSlice';
import { initializeAuthStore } from '../../../utils/authUtils';
import { sendToKinesis } from '../../../analytics/kinesisTransport';

const ConfirmationSchema = Yup.object().shape({
  code: Yup.string()
    .matches(/^\d+$/, 'Verification code must only contain numbers')
    .length(6, 'Verification code must be 6 digits')
    .required('Verification code is required'),
});

const ERROR_MESSAGES = {
  CodeMismatchException: 'Invalid verification code. Please try again.',
  ExpiredCodeException:
    'Verification code has expired. Please request a new one.',
  UserNotFoundException: 'No account exists with this email',
  NotAuthorizedException: 'Incorrect username or password',
  UserNotConfirmedException: 'Please confirm your account',
  DEFAULT: 'An unexpected error occurred. Please try again.',
};

function ConfirmSignUp({ userCredentials, onGoBack }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resendCooldown, setResendCooldown] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const handleLoginAttempt = async () => {
    try {
      const { isSignedIn } = await signIn({
        username: userCredentials.username,
        password: userCredentials.password,
      });

      if (isSignedIn) {
        const authData = await initializeAuthStore();
        if (!authData) {
          throw new Error('Failed to initialize authentication');
        }
        dispatch(login(authData));
        navigate('/landing');

        sendToKinesis('user_events', 'USER_LOGGED_IN', {
          userid: authData.user.userId,
        });
      }
    } catch (error) {
      const message = ERROR_MESSAGES[error.__type] || ERROR_MESSAGES.DEFAULT;
      setErrorMessage(message);
      throw new Error(message);
    }
  };

  const startResendCooldown = () => {
    setResendCooldown(30);
    const timer = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleConfirmation = async (values, { setSubmitting }) => {
    try {
      setErrorMessage('');
      await confirmSignUp({
        username: userCredentials.username,
        confirmationCode: values.code,
      });

      if (userCredentials?.isInitiatedFromLogin) {
        await handleLoginAttempt();
      } else {
        // Use signIn instead of autoSignIn so the token is issued AFTER
        // the POST_CONFIRMATION trigger (post_signup) has set
        // custom:organization_id on the user. autoSignIn reuses a
        // pre-confirmation token that doesn't have that attribute.
        await handleLoginAttempt();
      }
    } catch (error) {
      const message = ERROR_MESSAGES[error.name] || ERROR_MESSAGES.DEFAULT;
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;

    try {
      setErrorMessage(''); // Clear any existing errors
      await resendSignUpCode({ username: userCredentials.username });
      startResendCooldown();
    } catch (error) {
      setErrorMessage('Failed to resend verification code. Please try again.');
      console.error('Failed to resend code:', error);
    }
  };

  const renderVerificationForm = () => (
    <Formik
      initialValues={{ code: '' }}
      validationSchema={ConfirmationSchema}
      onSubmit={handleConfirmation}
    >
      {({ errors, touched, isSubmitting }) => (
        <Form className="cst-form">
          <h1 className="fs-4 fw-bold mb-2 text-center"> Verify Your Email</h1>
          <p className="text-muted mb-4 text-center">
            We&apos;ve sent a verification code to your email address. Please
            enter the code below to confirm your account.
          </p>

          <div className="mb-lg-4 mb-3">
            <label htmlFor="code" className="pb-1 small fw-semibold">
              Verification Code*
            </label>
            <Field
              type="text"
              className={`form-control ${
                touched.code && errors.code ? 'is-invalid border-danger' : ''
              }`}
              id="code"
              name="code"
              autoComplete="off"
              placeholder="Enter 6-digit code"
              maxLength="6"
            />
            {touched.code && errors.code && (
              <div className="invalid-feedback">{errors.code}</div>
            )}
          </div>

          {errorMessage && (
            <div className="alert alert-danger" role="alert">
              <span className="px-2">
                <IconAlertSquareRounded />
              </span>
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span 
                  className="spinner-border spinner-border-sm me-2" 
                  role="status" 
                  aria-hidden="true"
                />
                <span>Verifying...</span>
              </>
            ) : (
              'Verify Account'
            )}
          </button>

          <div className="text-center mt-3">
            <p className="mb-2">Didn&apos;t receive the code?</p>
            <button
              type="button"
              className="btn btn-link"
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
            >
              {resendCooldown > 0
                ? `Resend code in ${resendCooldown}s`
                : 'Resend code'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      <div className="personal_info">
        <Link onClick={onGoBack}>
          <IconChevronLeft /> Back
        </Link>
        {/* <p>Verify Your Account</p> */}
      </div>
      {renderVerificationForm()}
    </>
  );
}

ConfirmSignUp.propTypes = {
  userCredentials: PropTypes.shape({
    username: PropTypes.string.isRequired,
    password: PropTypes.string,
    isInitiatedFromLogin: PropTypes.bool,
  }).isRequired,
  onGoBack: PropTypes.func.isRequired,
};

export default ConfirmSignUp;
